// Hash string to number
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function generateAccentFromSeed(seed: string, style: "light" | "black" = "light") {
  const h = hashString(seed) % 360;
  const s = 70;
  const l = style === "light" ? 60 : 50;
  return { hue: h, color: `hsl(${h}, ${s}%, ${l}%)` };
}

export function getCoverGradient(seed: string, style: "light" | "black" = "light") {
  const h = hashString(seed) % 360;
  if (style === "light") {
    return `linear-gradient(135deg, hsl(${h}, 65%, 72%), hsl(${h + 35}, 60%, 58%))`;
  }
  return `linear-gradient(135deg, hsl(${h}, 55%, 45%), hsl(${h + 40}, 50%, 35%))`;
}

export function adjustColor(hex: string, amount: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, "0")}`;
}

export interface GiftTheme {
  bg: string;
  cardBg: string;
  cardShadow: string;
  cardBorder: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  shareBorder: string;
  shareHover: string;
  shareText: string;
  messageBg: string;
  messageBorder: string;
  glowColor: string;
  spotlightColor: string;
  particleColor: string;
  badgeBg: string;
  badgeText: string;
  ctaBg: string;
}

export const lightTheme: GiftTheme = {
  bg: "linear-gradient(180deg, #F8F7FF 0%, #F0EEFF 30%, #F5F5F7 100%)",
  cardBg: "rgba(255, 255, 255, 0.85)",
  cardShadow: "0 30px 80px rgba(108, 99, 255, 0.06), 0 4px 20px rgba(0,0,0,0.03)",
  cardBorder: "1px solid rgba(108, 99, 255, 0.08)",
  textPrimary: "#1A1A2E",
  textSecondary: "rgba(26, 26, 46, 0.6)",
  textTertiary: "rgba(26, 26, 46, 0.38)",
  shareBorder: "rgba(108, 99, 255, 0.12)",
  shareHover: "rgba(108, 99, 255, 0.05)",
  shareText: "rgba(26, 26, 46, 0.6)",
  messageBg: "rgba(108, 99, 255, 0.04)",
  messageBorder: "1px solid rgba(108, 99, 255, 0.08)",
  glowColor: "rgba(108, 99, 255, 0.12)",
  spotlightColor: "rgba(108, 99, 255, 0.08)",
  particleColor: "rgba(108, 99, 255, 0.4)",
  badgeBg: "rgba(108, 99, 255, 0.08)",
  badgeText: "rgba(108, 99, 255, 0.7)",
  ctaBg: "rgba(108, 99, 255, 0.05)",
};

export const blackTheme: GiftTheme = {
  bg: "linear-gradient(180deg, #0D0D14 0%, #111128 40%, #0A0A12 100%)",
  cardBg: "rgba(18, 18, 32, 0.8)",
  cardShadow: "0 40px 120px rgba(0,0,0,0.5), 0 0 60px rgba(212, 175, 55, 0.04)",
  cardBorder: "1px solid rgba(212, 175, 55, 0.12)",
  textPrimary: "rgba(255, 255, 255, 0.95)",
  textSecondary: "rgba(255, 255, 255, 0.6)",
  textTertiary: "rgba(255, 255, 255, 0.32)",
  shareBorder: "rgba(212, 175, 55, 0.15)",
  shareHover: "rgba(212, 175, 55, 0.08)",
  shareText: "rgba(255, 255, 255, 0.6)",
  messageBg: "rgba(212, 175, 55, 0.05)",
  messageBorder: "1px solid rgba(212, 175, 55, 0.1)",
  glowColor: "rgba(212, 175, 55, 0.1)",
  spotlightColor: "rgba(212, 175, 55, 0.06)",
  particleColor: "rgba(212, 175, 55, 0.5)",
  badgeBg: "rgba(212, 175, 55, 0.1)",
  badgeText: "rgba(212, 175, 55, 0.8)",
  ctaBg: "rgba(212, 175, 55, 0.06)",
};

export function getTheme(style: string): GiftTheme {
  return style === "black" ? blackTheme : lightTheme;
}

export function getAccentForStyle(style: string, baseAccent: string): string {
  if (style === "black") return "#D4AF37"; // gold
  return baseAccent || "#6C63FF"; // purple
}

/**
 * Склонение русского имени в родительный падеж (от кого?)
 * Покрывает основные паттерны мужских и женских имён.
 */
export function toGenitive(name: string): string {
  if (!name) return name;
  const trimmed = name.trim();
  
  // Женские имена на -ия (Мария → Марии, Виктория → Виктории)
  if (/ия$/i.test(trimmed)) return trimmed.slice(0, -1) + 'и';
  // Женские на -ья (Дарья → Дарьи)
  if (/ья$/i.test(trimmed)) return trimmed.slice(0, -1) + 'и';
  // Женские на -а после согласной (Елена → Елены, Анна → Анны, Ольга → Ольги)
  if (/[бвгджзклмнпрстфхцчшщ]а$/i.test(trimmed)) {
    const base = trimmed.slice(0, -1);
    const lastConsonant = base.slice(-1).toLowerCase();
    // г, к, х → и (Ольга → Ольги)
    if ('гкх'.includes(lastConsonant)) return base + 'и';
    // ж, ш, щ, ч → и
    if ('жшщч'.includes(lastConsonant)) return base + 'и';
    return base + 'ы';
  }
  // Женские на -я (Катя → Кати, Настя → Насти)
  if (/я$/i.test(trimmed)) return trimmed.slice(0, -1) + 'и';
  // Женские на -а после гласной (Зоя handled by -я above)
  if (/а$/i.test(trimmed)) return trimmed.slice(0, -1) + 'ы';
  // Мужские на -й (Андрей → Андрея, Сергей → Сергея, Дмитрий → Дмитрия)
  if (/ий$/i.test(trimmed)) return trimmed.slice(0, -2) + 'ия';
  if (/й$/i.test(trimmed)) return trimmed.slice(0, -1) + 'я';
  // Мужские на -ь (Игорь → Игоря)
  if (/ь$/i.test(trimmed)) return trimmed.slice(0, -1) + 'я';
  // Мужские на согласную (Антон → Антона, Максим → Максима)
  if (/[бвгджзклмнпрстфхцчшщ]$/i.test(trimmed)) return trimmed + 'а';
  
  // Fallback — оставить как есть
  return trimmed;
}
